import { Image, message, Spin } from 'antd';
import { useTranslation } from 'next-i18next';
import { memo, useEffect, useState } from 'react';

import { getThreadsUserPostList } from '@/lib/service/threads';
import { cn } from '@/lib/utils';
import { ThreadsPostItemModelV1, ThreadsPostListModelV1 } from '@/types/threads';
import { fallbackImage, imageProxyUrl } from '../shared';
import ThreadsPostHeader from '../ThreadsPostHeader';

interface ThreadsPostRecommendProps {
  mediaName: string;
  existCode: string;
  onClickPost: (username: string, post: ThreadsPostItemModelV1) => void;
}

const ThreadsPostRecommend = ({ mediaName, existCode, onClickPost }: ThreadsPostRecommendProps) => {
  const { t } = useTranslation();
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recommendList, setRecommendList] = useState<ThreadsPostListModelV1>();

  const initComponentData = () => {
    if (!mediaName || !existCode) {
      return;
    }

    setLoading(true);
    getThreadsUserPostList(mediaName, { existCode })
      .then((res) => {
        if (res.code === 0) {
          setRecommendList(res.data);
          setReady(true);
        } else {
          message.error(res.message);
        }
      })
      .catch((err) => {
        message.error(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    initComponentData();
  }, [mediaName, existCode]);

  if (!recommendList || !recommendList.length) {
    return null;
  }

  return (
    <div className="max-w-[984px] mx-auto mt-12">
      <Spin spinning={loading}>
        {ready && (
          <div className="mb-8 text-xl font-bold">
            {t('More Posts from')} @{mediaName}
          </div>
        )}
        <div className="p-8 grid grid-cols-1 gap-8 border border-gray-200 rounded-lg shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)] max-md:px-[18px] max-md:py-6">
          {recommendList.map((post, index) => (
            <div
              key={post.code}
              className={cn('border-gray-200 cursor-pointer', {
                'pt-8 border-t': index > 0,
              })}
              onClick={() => onClickPost(mediaName, post)}
            >
              {/* 帖子头部信息 */}
              <ThreadsPostHeader mediaName={mediaName} post={post} />

              {/* 帖子媒体内容封面列表 */}
              <div className="flex gap-2 overflow-x-auto mini-scrollbar">
                {post?.media_list?.map((item, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 flex justify-center items-center rounded-md overflow-hidden"
                  >
                    <Image
                      src={imageProxyUrl(item.media_type === 2 ? item.cover_image : item.url)}
                      alt={item.url}
                      width={282}
                      height={186}
                      style={{ objectFit: 'cover' }}
                      preview={false}
                      fallback={fallbackImage}
                      className="flex-shrink-0"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Spin>
    </div>
  );
};

export default memo(ThreadsPostRecommend, (prevProps, nextProps) => {
  return prevProps.mediaName === nextProps.mediaName && prevProps.existCode === nextProps.existCode;
});
